/**
 * Crossword puzzle data contract — the seam between the two build agents.
 *
 * The Data & Generation agent produces puzzles that conform to this shape; the
 * Game & Polish agent's engine + renderer consume it. NOTHING about feel,
 * rendering, or solve-state lives here — this is the data format only. Keep it
 * stable; if it genuinely must change, change it here once and both sides
 * follow. See CONTRACT.md in this folder.
 *
 * Conventions (American crossword rules the generator must satisfy and the
 * renderer may assume):
 *  - Grids are rectangular, addressed row-major as grid[row][col], zero-based.
 *  - Black ("block") squares carry no letter. White squares carry one
 *    uppercase A–Z solution letter.
 *  - Every white cell is "checked" — part of BOTH an across and a down word.
 *  - Minimum word length is 3. No unchecked cells, no two-letter words.
 *  - A cell is NUMBERED iff it starts an across word (no white cell to its
 *    left, a white cell to its right) and/or a down word (no white cell above,
 *    a white cell below). Numbers run left-to-right, top-to-bottom from 1.
 *  - Across and down clues SHARE the numbering (1-Across and 1-Down may both
 *    start on cell 1).
 */
/**
 * Structural validation — the shared invariant BOTH sides can run. Returns a
 * list of human-readable problems; an empty array means the puzzle is
 * well-formed. The generator must emit only puzzles that pass this; the engine
 * may trust a passing puzzle. This checks STRUCTURE, not taste — it does not
 * judge fill quality or clue cleverness.
 */
export function validatePuzzle(p) {
    const problems = [];
    const at = (r, c) => p.grid[r]?.[c];
    const tag = (cl) => `${cl.number}${cl.direction[0]}`;
    if (p.grid.length !== p.rows) {
        problems.push(`grid has ${p.grid.length} rows, expected ${p.rows}`);
    }
    p.grid.forEach((row, r) => {
        if (row.length !== p.cols) {
            problems.push(`row ${r} has ${row.length} cols, expected ${p.cols}`);
        }
    });
    // White cells must carry a single A–Z solution letter.
    for (let r = 0; r < p.rows; r += 1) {
        for (let c = 0; c < p.cols; c += 1) {
            const cell = at(r, c);
            if (!cell || cell.block)
                continue;
            if (!cell.solution || !/^[A-Z]$/.test(cell.solution)) {
                problems.push(`white cell ${r},${c} has invalid solution ${JSON.stringify(cell.solution)}`);
            }
        }
    }
    // Each clue must stay in bounds, be >= 3 letters, and match the grid letters
    // along its run.
    for (const clue of p.clues) {
        if (clue.length < 3)
            problems.push(`clue ${tag(clue)} is shorter than 3`);
        if (clue.answer.length !== clue.length)
            problems.push(`clue ${tag(clue)} answer/length mismatch`);
        for (let i = 0; i < clue.length; i += 1) {
            const r = clue.row + (clue.direction === 'down' ? i : 0);
            const c = clue.col + (clue.direction === 'across' ? i : 0);
            const cell = at(r, c);
            if (!cell || cell.block) {
                problems.push(`clue ${tag(clue)} runs off-grid or into a block at ${r},${c}`);
                break;
            }
            if (cell.solution !== clue.answer[i]) {
                problems.push(`clue ${tag(clue)} letter ${i} (${clue.answer[i]}) != grid (${cell.solution}) at ${r},${c}`);
            }
        }
    }
    return problems;
}
//# sourceMappingURL=schema.js.map